import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Heart, Link2, Users, UserPlus, UserCheck } from 'lucide-react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabaseClient';
import { slugify } from '../../utils/slug';
import { routeBuilders } from '../routes';

type CreatorProfile = {
  id: string;
  display_name: string | null;
  avatar_url?: string | null;
  bio?: string | null;
  role?: string | null;
  social_links?: Record<string, string> | null;
};

type CreatorSeries = {
  id: string;
  title: string;
  slug?: string | null;
  description?: string | null;
  cover_image_url?: string | null;
  status?: string | null;
  created_at?: string | null;
  episodes?: CreatorEpisode[];
};

type CreatorEpisode = {
  id: string;
  title: string;
  slug?: string | null;
  cover_image_url?: string | null;
  description?: string | null;
  duration_seconds?: number | null;
  episode_number?: number | null;
  status?: string | null;
  created_at?: string | null;
  published_at?: string | null;
};

type SeriesCard = {
  id: string;
  title: string;
  slug?: string | null;
  coverImage: string;
  description?: string | null;
  episodeCount: number;
  publishedAt?: string | null;
};

const FALLBACK_COVER =
  'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=800&q=80';
const IMAGES_BUCKET = 'Images';

const extractImageStoragePath = (raw?: string | null): string | null => {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (!trimmed.startsWith('http')) {
    return trimmed.replace(/^\/+/, '');
  }
  const bucketSegment = `/${IMAGES_BUCKET}/`;
  const bucketIndex = trimmed.indexOf(bucketSegment);
  if (bucketIndex === -1) return null;
  const withoutParams = trimmed.slice(bucketIndex + bucketSegment.length).split('?')[0];
  return decodeURIComponent(withoutParams);
};

const getDisplayImageUrl = async (raw?: string | null): Promise<string | null> => {
  if (!raw) return null;
  const storagePath = extractImageStoragePath(raw);
  if (!storagePath) return raw;
  const { data, error } = await supabase.storage
    .from(IMAGES_BUCKET)
    .createSignedUrl(storagePath, 60 * 60 * 24 * 7);
  if (error || !data?.signedUrl) {
    console.error('Unable to generate signed URL for image', error);
    return raw;
  }
  return data.signedUrl;
};

const safeDateValue = (value?: string | null) => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
};

const countFollowers = async (creatorId: string): Promise<number> => {
  const { count, error } = await supabase
    .from('creator_follows')
    .select('follower_id', { count: 'exact', head: true })
    .eq('creator_id', creatorId);
  if (error) {
    const message = (error as any)?.message || '';
    const isMissingTable = message.includes('does not exist') || error.code === '42P01';
    if (!isMissingTable) {
      console.warn('Follower count failed on creator_follows', error);
    }
    return 0;
  }
  return count ?? 0;
};

const countLikesForSeries = async (seriesIds: string[]): Promise<number> => {
  if (!seriesIds.length) return 0;
  const { count, error } = await supabase
    .from('likes')
    .select('id', { count: 'exact', head: true })
    .in('series_id', seriesIds);
  if (error) {
    console.error('Error counting likes for creator series', error);
    return 0;
  }
  return count ?? 0;
};

const FOLLOW_TABLES = ['creator_follows'];

const checkFollowState = async (creatorId: string, userId: string): Promise<boolean> => {
  const { data, error } = await supabase
    .from('creator_follows')
    .select('follower_id')
    .eq('creator_id', creatorId)
    .eq('follower_id', userId)
    .limit(1);
  if (error) {
    const message = (error as any)?.message || '';
    const isMissingTable = message.includes('does not exist') || error.code === '42P01';
    if (!isMissingTable) {
      console.warn('Unable to check follow state', error);
    }
    return false;
  }
  return Boolean(data && data.length > 0);
};

const insertFollow = async (creatorId: string, userId: string) => {
  const payload = { creator_id: creatorId, follower_id: userId };
  const { error } = await supabase.from('creator_follows').insert(payload);
  if (!error || error?.code === '23505') return true;
  throw error ?? new Error('Unable to follow creator.');
};

const deleteFollow = async (creatorId: string, userId: string) => {
  const { error } = await supabase
    .from('creator_follows')
    .delete()
    .eq('creator_id', creatorId)
    .eq('follower_id', userId);
  if (error) {
    const message = (error as any)?.message || '';
    const isMissingTable = message.includes('does not exist') || error.code === '42P01';
    if (isMissingTable) return false;
    throw error;
  }
  return true;
};

const fetchCreatorSeries = async (creatorId: string): Promise<CreatorSeries[]> => {
  const { data, error } = await supabase
    .from('series')
    .select(
      `
      id,
      title,
      description,
      slug,
      cover_image_url,
      status,
      created_at,
      episodes (
        id,
        title,
        slug,
        cover_image_url,
        description,
        duration_seconds,
        episode_number,
        status,
        created_at,
        published_at
      )
    `,
    )
    .eq('creator_id', creatorId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching creator series', error);
    return [];
  }
  return data ?? [];
};

const buildSeriesCards = async (seriesList: CreatorSeries[]): Promise<SeriesCard[]> => {
  const seriesWithCovers = await Promise.all(
    seriesList.map(async (series) => ({
      ...series,
      cover_image_url: await getDisplayImageUrl(series.cover_image_url),
    })),
  );

  const cards = seriesWithCovers.map((series) => {
    const publishedEpisodes = (series.episodes ?? []).filter(
      (ep) => (ep.status ?? '').toLowerCase() === 'published',
    );
    const fallbackPublished = publishedEpisodes[0]?.published_at ?? publishedEpisodes[0]?.created_at ?? null;
    return {
      id: series.id,
      title: series.title,
      slug: series.slug ?? slugify(series.title),
      coverImage: series.cover_image_url ?? FALLBACK_COVER,
      description: series.description ?? undefined,
      episodeCount: publishedEpisodes.length,
      publishedAt: series.created_at ?? fallbackPublished,
    };
  });

  return cards.sort((a, b) => safeDateValue(b.publishedAt) - safeDateValue(a.publishedAt)).slice(0, 8);
};

interface CreatorLandingProps {
  session: Session | null;
}

export function CreatorLanding({ session }: CreatorLandingProps) {
  const { creatorSlug = '' } = useParams<{ creatorSlug: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<CreatorProfile | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [followerCount, setFollowerCount] = useState(0);
  const [likeCount, setLikeCount] = useState(0);
  const [latestSeries, setLatestSeries] = useState<SeriesCard[]>([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followError, setFollowError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const socialLinks = useMemo(() => {
    if (!profile?.social_links) return [];
    return Object.entries(profile.social_links).filter(
      ([, value]) => typeof value === 'string' && Boolean(value?.trim()),
    );
  }, [profile?.social_links]);

  const viewerId = session?.user?.id ?? null;
  const isOwnProfile = Boolean(viewerId && profile?.id === viewerId);

  useEffect(() => {
    let isMounted = true;
    const loadCreator = async () => {
      setError(null);
      setLoading(true);
      try {
        const slugParam = decodeURIComponent(creatorSlug || '').toLowerCase();
        const searchTerm = slugParam.replace(/-/g, ' ').trim();
        const searchPattern = searchTerm ? `%${searchTerm.replace(/\s+/g, '%')}%` : '%';

        const { data: profiles, error: profileError } = await supabase
          .from('profiles')
          .select('id, display_name, avatar_url, bio, role, social_links')
          .eq('role', 'creator')
          .ilike('display_name', searchPattern)
          .limit(12);
        if (profileError) {
          throw profileError;
        }

        const match = (profiles ?? []).find((p) => slugify(p.display_name || p.id) === slugParam) ?? null;
        if (!match) {
          setError('Creator not found.');
          setLoading(false);
          return;
        }

        const creatorProfile = match;
        const canonicalSlug = slugify(creatorProfile.display_name || creatorProfile.id);
        if (canonicalSlug !== slugParam) {
          navigate(routeBuilders.creatorPublic(canonicalSlug), { replace: true });
        }

        const [resolvedAvatar, followers, series] = await Promise.all([
          getDisplayImageUrl(creatorProfile.avatar_url ?? null),
          countFollowers(creatorProfile.id),
          fetchCreatorSeries(creatorProfile.id),
        ]);
        const publishedSeries = series.filter(
          (s) => (s.status ?? '').toLowerCase() === 'published',
        );
        const [likes, seriesCards] = await Promise.all([
          countLikesForSeries(publishedSeries.map((s) => s.id)),
          buildSeriesCards(publishedSeries),
        ]);

        if (!isMounted) return;
        setProfile(creatorProfile);
        setAvatarUrl(resolvedAvatar);
        setFollowerCount(followers);
        setLikeCount(likes);
        setLatestSeries(seriesCards);
      } catch (err) {
        console.error('Unable to load creator page', err);
        if (isMounted) {
          setError('Unable to load this creator right now. Please try again shortly.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadCreator();
    return () => {
      isMounted = false;
    };
  }, [creatorSlug, navigate]);

  useEffect(() => {
    let active = true;
    if (!profile?.id || !viewerId || isOwnProfile) {
      setIsFollowing(false);
      return;
    }
    (async () => {
      const followed = await checkFollowState(profile.id, viewerId);
      if (active) {
        setIsFollowing(followed);
      }
    })();
    return () => {
      active = false;
    };
  }, [isOwnProfile, profile?.id, viewerId]);

  if (loading) {
    return (
      <div className="page-shell flex items-center justify-center min-h-[60vh]">
        <div className="space-y-3 text-center">
          <div className="w-16 h-16 border-4 border-base-300 border-t-primary rounded-full animate-spin mx-auto" />
          <p className="text-base-content/60">Loading creator profile...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="page-shell flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-3">
          <p className="text-lg font-semibold">Creator page unavailable</p>
          <p className="text-base-content/60">{error ?? 'The requested creator could not be found.'}</p>
        </div>
      </div>
    );
  }

  const displayName = profile.display_name || 'Creator';

  const handleToggleFollow = async () => {
    if (!profile?.id || isOwnProfile) return;
    if (!viewerId) {
      setFollowError('Sign in to follow creators.');
      return;
    }
    setFollowLoading(true);
    setFollowError(null);
    try {
      if (isFollowing) {
        const success = await deleteFollow(profile.id, viewerId);
        if (!success) {
          throw new Error('Unable to unfollow right now.');
        }
        setIsFollowing(false);
        setFollowerCount((count) => Math.max(0, count - 1));
      } else {
        await insertFollow(profile.id, viewerId);
        setIsFollowing(true);
        setFollowerCount((count) => count + 1);
      }
    } catch (err: any) {
      setFollowError(err?.message ?? 'Unable to update follow state right now.');
    } finally {
      setFollowLoading(false);
    }
  };

  return (
    <div className="page-shell">
      <div className="max-w-4xl mx-auto space-y-10 text-center relative">
        {!isOwnProfile && (
          <div className="absolute right-0 top-0 flex flex-col items-end gap-2">
            <button
              onClick={handleToggleFollow}
              disabled={followLoading}
              className={`btn btn-sm ${isFollowing ? 'btn-primary' : 'btn-outline btn-primary'} ${
                followLoading ? 'opacity-80 cursor-not-allowed' : ''
              }`}
              aria-pressed={isFollowing}
            >
              {isFollowing ? <UserCheck className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
              <span>{isFollowing ? 'Following' : 'Follow'}</span>
            </button>
            {followError && <p className="text-xs text-error">{followError}</p>}
          </div>
        )}
        <header className="space-y-4">
          <div className="w-28 h-28 mx-auto rounded-full overflow-hidden bg-base-200 ring-2 ring-base-300">
            <img
              src={avatarUrl ?? FALLBACK_COVER}
              alt={`${displayName} avatar`}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="space-y-2">
            <h1 className="text-4xl font-bold">{displayName}</h1>
            <div className="flex items-center justify-center gap-6 text-sm text-base-content/70">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>{followerCount.toLocaleString()} followers</span>
              </div>
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4" />
                <span>{likeCount.toLocaleString()} likes</span>
              </div>
            </div>
          </div>
        </header>

        <section className="rounded-3xl border border-base-200 bg-base-100 shadow-sm p-6 text-left">
          <h2 className="text-xl font-semibold mb-3">About</h2>
          <p className="text-base-content/80 leading-relaxed">
            {profile.bio?.trim() || 'This creator has not added a bio yet.'}
          </p>
        </section>

        <section className="rounded-3xl border border-base-200 bg-base-100 shadow-sm p-6 text-left space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Social links</h2>
            <Link2 className="w-4 h-4 text-base-content/50" />
          </div>
          {socialLinks.length === 0 ? (
            <p className="text-sm text-base-content/60">No social links provided.</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {socialLinks.map(([platform, url]) => (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outline btn-primary btn-sm capitalize"
                >
                  {platform}
                </a>
              ))}
            </div>
          )}
        </section>

        <section className="space-y-4 text-left">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Latest series</h2>
              <p className="text-sm text-base-content/60">Newest releases from {displayName}</p>
            </div>
            <span className="text-sm text-base-content/50">{latestSeries.length} listed</span>
          </div>

          {latestSeries.length === 0 ? (
            <div className="rounded-3xl border border-base-200 bg-base-200 p-8 text-center text-base-content/60">
              No published series yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {latestSeries.map((series) => (
                <Link
                  key={series.id}
                  to={routeBuilders.seriesPublic(series.slug ?? slugify(series.title))}
                  className="rounded-3xl border border-base-200 bg-base-100 shadow-sm p-4 flex gap-3 hover:shadow-md transition-shadow"
                >
                  <div className="w-20 h-20 rounded-2xl overflow-hidden bg-base-200 flex-shrink-0">
                    <img
                      src={series.coverImage}
                      alt={series.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0 space-y-1">
                    <h3 className="font-semibold leading-snug line-clamp-2">{series.title}</h3>
                    <p className="text-xs text-base-content/60">{series.episodeCount} published episodes</p>
                    {series.description && (
                      <p className="text-sm text-base-content/70 line-clamp-3">{series.description}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
